'use client';

import { useTranslations } from 'next-intl';
import { type FC, useId, useState } from 'react';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { ToolSettingsRadioGroup } from '../../_components/ToolSettingsRadioGroup';
import { CHARACTER_TAG_OPTIONS } from '../../_constants';
import type { CharacterElementTag } from '../types';

interface CharacterInfoConfigProps {
  name: string;
  tag: CharacterElementTag;
  voiceId?: string;
  onNameChange: (value: string) => void;
  onTagChange: (value: CharacterElementTag) => void;
  onVoiceIdChange: (value: string | undefined) => void;
}

export const CharacterInfoConfig: FC<CharacterInfoConfigProps> = ({
  name,
  tag,
  voiceId,
  onNameChange,
  onTagChange,
  onVoiceIdChange,
}) => {
  const t = useTranslations();
  const nameInputId = useId();
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <span className="font-medium text-sm text-system-white leading-[1.4]">
          {t('tools.characterCreation.info.tag')}
        </span>
        <ToolSettingsRadioGroup
          value={tag}
          options={CHARACTER_TAG_OPTIONS.map((option) => ({
            value: option.id,
            label: t(option.labelKey),
          }))}
          onValueChange={(nextValue) => onTagChange(nextValue as CharacterElementTag)}
          className="h-12 rounded-10 border border-system-white08 light:border-system-black08 bg-system-white04 light:bg-system-black04"
          optionsWrapperClassName="h-full rounded-10 hover:bg-system-white08 light:hover:bg-system-black08"
          activeOptionClassName="rounded-10 border-[#6673FF] bg-[rgba(87,101,242,0.24)] light:bg-[rgba(87,101,242,0.16)]"
          labelClassName="light:text-system-black text-system-white"
          renderContent={(option) => (
            <span className="font-medium text-sm text-system-white leading-[1.4]">{option.label}</span>
          )}
        />
      </div>

      {tag === 'role' && (
        <div className="flex flex-col gap-2">
          <span className="font-medium text-sm text-system-white leading-[1.4]">
            {t('tools.characterCreation.info.voice')}
          </span>
          <VoiceSelectionDialog
            open={isVoiceDialogOpen}
            onOpenChange={setIsVoiceDialogOpen}
            title={t('tools.characterCreation.info.voice')}
            value={voiceId}
            allowNoVoice
            noSoundLabel={t('tools.characterCreation.info.noVoice')}
            onConfirm={(nextVoiceId) => onVoiceIdChange(nextVoiceId)}
            voiceInfoEditFieldSuffix="character-creation"
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label
          htmlFor={nameInputId}
          className="flex flex-col gap-2 font-medium text-sm text-system-white leading-[1.4]"
        >
          {t('tools.characterCreation.info.name')}
          <input
            id={nameInputId}
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t('tools.characterCreation.info.namePlaceholder')}
            maxLength={50}
            className="h-12 w-full rounded-10 border border-system-white08 bg-system-white04 px-3 font-normal text-sm text-system-white placeholder:text-system-white/40 focus:border-system-white/20 focus:outline-none"
          />
        </label>
      </div>
    </div>
  );
};
